// src/contexts/HolidayContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import holidayService from '../services/holiday.service';

const HolidayContext = createContext();

export const useHolidays = () => {
    const context = useContext(HolidayContext);
    if (!context) {
        throw new Error('useHolidays must be used within a HolidayProvider');
    }
    return context;
};

const HOLIDAY_STORAGE_KEY = 'hrportal_holidays';

const loadHolidaysFromStorage = () => {
    try {
        const stored = localStorage.getItem(HOLIDAY_STORAGE_KEY);
        return stored ? JSON.parse(stored) : getDefaultHolidays();
    } catch (error) {
        console.error('Error loading holidays:', error);
        return getDefaultHolidays();
    }
};

const saveHolidaysToStorage = (holidays) => {
    try {
        localStorage.setItem(HOLIDAY_STORAGE_KEY, JSON.stringify(holidays));
    } catch (error) {
        console.error('Error saving holidays:', error);
    }
};

// Default holidays for 2025
const getDefaultHolidays = () => {
    return [
        { id: '1', date: '2025-01-26', name: 'Republic Day', type: 'national', description: 'National Holiday' },
        { id: '2', date: '2025-03-14', name: 'Holi', type: 'national', description: 'Festival of Colors' },
        { id: '3', date: '2025-03-31', name: 'Eid ul-Fitr', type: 'national', description: 'Islamic Festival' },
        { id: '4', date: '2025-04-14', name: 'Ambedkar Jayanti', type: 'national', description: 'National Holiday' },
        { id: '5', date: '2025-04-18', name: 'Good Friday', type: 'national', description: 'Christian Holiday' },
        { id: '6', date: '2025-05-01', name: 'May Day', type: 'national', description: 'Labour Day' },
        { id: '7', date: '2025-08-15', name: 'Independence Day', type: 'national', description: 'National Holiday' },
        { id: '8', date: '2025-08-27', name: 'Janmashtami', type: 'special', description: 'Krishna Janmashtami' },
        { id: '9', date: '2025-10-02', name: 'Gandhi Jayanti', type: 'national', description: 'National Holiday' },
        { id: '10', date: '2025-10-22', name: 'Dussehra', type: 'national', description: 'Festival Holiday' },
        { id: '11', date: '2025-11-12', name: 'Diwali', type: 'national', description: 'Festival of Lights' },
        { id: '12', date: '2025-12-25', name: 'Christmas', type: 'national', description: 'Christian Holiday' }
    ];
};

export const HolidayProvider = ({ children }) => {
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(false);

    // Load from localStorage initially (synchronous)
    useEffect(() => {
        const stored = loadHolidaysFromStorage();
        setHolidays(stored);
    }, []);

    // Fetch holidays from backend when authenticated
    const fetchHolidays = async () => {
        // Only fetch if user is authenticated
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
            //console.log('⚠️ No auth token, using localStorage holidays');
            return;
        }

        try {
            setLoading(true);
            const data = await holidayService.getAllHolidays();

            // Map backend format to frontend format
            const mappedHolidays = data.map(h => ({
                id: h.id,
                date: h.date || h.holiday_date,
                name: h.name || h.holiday_name,
                type: h.day_type || h.holiday_type || 'national',
                description: h.description || ''
            }));

            setHolidays(mappedHolidays);

            // Also save to localStorage for offline access
            if (mappedHolidays.length > 0) {
                saveHolidaysToStorage(mappedHolidays);
            }
        } catch (error) {
            console.error('Error fetching holidays, using localStorage:', error);
            // Keep existing holidays from localStorage
        } finally {
            setLoading(false);
        }
    };

    const addHoliday = (holiday) => {
        const newHoliday = {
            ...holiday,
            id: Date.now().toString()
        };
        setHolidays(prev => [...prev, newHoliday]);
    };

    const updateHoliday = (id, updatedHoliday) => {
        setHolidays(prev => prev.map(h => h.id === id ? { ...h, ...updatedHoliday } : h));
    };

    const deleteHoliday = (id) => {
        setHolidays(prev => prev.filter(h => h.id !== id));
    };

    const getHolidaysByMonth = (year, month) => {
        return holidays.filter(holiday => {
            // Use string parsing to avoid UTC timezone shift issues
            // new Date("YYYY-MM-DD") parses as UTC which shifts dates for IST
            const [hYear, hMonth] = holiday.date.split('-').map(Number);
            return hYear === year && (hMonth - 1) === month;
        });
    };

    const getHolidaysByYear = (year) => {
        return holidays.filter(holiday => {
            // Use string parsing to avoid UTC timezone shift issues
            const [hYear] = holiday.date.split('-').map(Number);
            return hYear === year;
        });
    };

    const isHoliday = (dateString) => {
        return holidays.some(holiday => holiday.date === dateString);
    };

    const getHolidayByDate = (dateString) => {
        return holidays.find(holiday => holiday.date === dateString);
    };

    const value = {
        holidays,
        loading,
        fetchHolidays,
        addHoliday,
        updateHoliday,
        deleteHoliday,
        getHolidaysByMonth,
        getHolidaysByYear,
        isHoliday,
        getHolidayByDate
    };

    return (
        <HolidayContext.Provider value={value}>
            {children}
        </HolidayContext.Provider>
    );
};
